// src/theme.ts
import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#020511',
    },
    secondary: {
      main: 'rgba(2, 5, 17, 0.15)',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 768,
      md: 820,
      lg: 1200,
      xl: 1920,
    },
  },
  typography: {
    fontFamily: "'Instrument Sans', sans-serif",
    allVariants: {
      color: '#020511',
    },
    h1: {
      fontWeight: 700,
      fontSize: '24px',
      lineHeight: '29.28px',
      color: 'var(--Interviewer-Black)',
    },
    h2: {
      fontWeight: 700,
      fontSize: '20px',
      lineHeight: '24.4px',
      color: 'var(--Interviewer-Black)',
    },
    h3: {
      fontWeight: 700,
      fontSize: '15px',
      lineHeight: '18.3px',
      color: 'var(--Interviewer-Black)',
      letterSpacing: '-0.02em',
    },
    h4: {
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '20px',
      color: 'var(--Interviewer-Lighter-Black)',
    },
    h5: {
      fontWeight: 500,
      fontSize: '12px',
      lineHeight: '16px',
      color: 'var(--Interviewer-Lighter-Black)',
    },
    body1: {
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '17px',
      color: 'var(--Interviewer-Lighter-Black)',
    },
    body2: {
      fontWeight: 500,
      fontSize: '12px',
      lineHeight: '20px',
      color: 'var(--Interviewer-Lightest-Black)',
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          fontSize: '14px',
          lineHeight: '17px',
          fontWeight: 600,
          borderRadius: '8px',
          padding: '12px 48px',
          height: '41px',
          whiteSpace: 'nowrap',
          textTransform: 'none',
        },
        sizeLarge: {
          height: '52px',
          fontSize: '16px',
        },
        contained: {
          border: '1px solid #FFFFFF1A',
          background:
            'linear-gradient(0deg, var(--Interviewer-Black, #020511), var(--Interviewer-Black, #020511)), linear-gradient(360deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.2) 100%)',

          '&:hover': {
            background:
              'linear-gradient(0deg, var(--Interviewer-Black, #020511), var(--Interviewer-Black, #020511)), linear-gradient(360deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.1) 100%)',
          },

          '&:disabled': {
            background: 'var(--Interviewer-Near-White)',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {},
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&:focus $notchedOutline': {
            border: '1px solid var(--Interviewer-Green)',
          },
          '&:hover $notchedOutline': {
            borderColor: 'var(--Interviewer-Green)',
          },
        },
        input: {
          padding: '12px',
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '17px',
          color: 'var(--Interviewer-Black)',
        },
        notchedOutline: {
          borderRadius: '8px',
          border: '1px solid var(--Interviewer-Black)',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '17px',
          color: 'var(--Interviewer-Lightest-Black)',

          '&$focused': {
            color: '#3D3D3C',
            top: '0',
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          margin: 0,
          '& .MuiTypography-root': {
            fontWeight: 500,
            fontSize: '12px',
            lineHeight: '14.64px',
            color: 'var(--Interviewer-Lighter-Black)',
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '17px',
          color: 'var(--Interviewer-Green)',
          textDecoration: 'none',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          width: '14px',
          height: '14px',
          '& .MuiSvgIcon-root': {
            fontSize: '14px',
            '& path': {
              strokeWidth: '0.83px',
            },
          },
          '& .MuiSvgIcon-root path': {
            stroke: '#02051126',
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          height: '48px',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          height: '48px',
          borderRadius: '12px',
          padding: '12px',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: '#fff',
          borderBottom: '1px solid var(--Interviewer-Near-White)',
          boxShadow: '0px 1px 4px 0px #0000001A',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        action: {
          margin: 'auto 0',
        },
      },
    },
  },
});

export const globalStyles = {
  ':root': {
    '--Interviewer-Black': '#020511',
    '--Interviewer-Near-White': '#02051126',
    '--Interviewer-Lightest-Black': '#02051159',
    '--Interviewer-Lighter-Black': '#020511A6',
    '--Interviewer-Green': '#31C07D',
    '--Light-Gray': '#F0F1F3',
    '--Lightest-Green': '#31C07D26',
    '--Lighter-Green': '#31C07D59',
    '--Red-Error-Color': '#F53C2D',
  },
  body: {
    overflowY: 'scroll',
    '-ms-overflow-style': 'none',
  },
  'body::-webkit-scrollbar': {
    display: 'none',
  },
};

export default theme;
